/**
 * Android Google Sign-In bridge.
 *
 * Google blocks OAuth inside Capacitor WebViews (Error 400 malformed).
 * Flow:
 * 1) App opens /auth-bridge.html?session=<uuid> in Chrome Custom Tabs
 * 2) Bridge writes Google OAuth tokens to Firestore authHandoffs/{session}
 * 3) App polls that doc on resume (deep link optional) → signInWithCredential
 *
 * Session id is persisted in localStorage so WebView reloads still finish sign-in.
 */
import { Capacitor } from "@capacitor/core";
import { GoogleAuthProvider, signInWithCredential } from "firebase/auth";
import { deleteDoc, doc, getDoc } from "firebase/firestore";
import { getDb, getFirebaseAuth } from "./config";

export const AUTH_BRIDGE_SCHEME = "com.pianotutor.ultimateclassic";
export const AUTH_BRIDGE_HOST = "auth-callback";
export const AUTH_BRIDGE_URL = "https://ultimate-piano-tutor.web.app/auth-bridge.html";

const HANDOFF_POLL_MS = 700;
const HANDOFF_TIMEOUT_MS = 3 * 60 * 1000;
const SESSION_STORAGE_KEY = "upt_android_auth_session";

export function isCapacitorAndroid(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
  } catch {
    return false;
  }
}

function saveSession(session: string): void {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, session);
  } catch {
    /* */
  }
}

function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* */
  }
}

function loadSession(): string | null {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function newSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function paramFromUrl(url: string, key: string): string | null {
  try {
    if (!url.includes("auth-callback") && !url.includes(`${key}=`) && !url.includes("session=")) {
      return null;
    }
    const normalized = url.includes("://") ? url : `${AUTH_BRIDGE_SCHEME}://${url}`;
    const u = new URL(normalized);
    const q = u.searchParams.get(key);
    if (q) return q;
    const hash = new URLSearchParams(u.hash.replace(/^#/, ""));
    return hash.get(key);
  } catch {
    try {
      const m = new RegExp(`[?&#]${key}=([^&]+)`).exec(url);
      return m ? decodeURIComponent(m[1]) : null;
    } catch {
      return null;
    }
  }
}

export function parseAuthBridgeDeepLink(url: string): { code?: string; idToken?: string } | null {
  const code = paramFromUrl(url, "code") || paramFromUrl(url, "session");
  if (code) return { code };
  const idToken = paramFromUrl(url, "idToken");
  if (idToken) return { idToken };
  return null;
}

type HandoffData = {
  idToken?: string | null;
  accessToken?: string | null;
  exp?: number;
};

async function readHandoff(code: string): Promise<HandoffData | null> {
  const db = getDb();
  if (!db) throw new Error("Firestore not configured.");
  const snap = await getDoc(doc(db, "authHandoffs", code));
  if (!snap.exists()) return null;
  return snap.data() as HandoffData;
}

async function consumeHandoff(code: string): Promise<HandoffData> {
  const data = await readHandoff(code);
  if (!data) throw new Error("Sign-in expired. Tap Sign in again.");
  const db = getDb();
  if (db) {
    try {
      await deleteDoc(doc(db, "authHandoffs", code));
    } catch {
      /* best-effort */
    }
  }
  if (typeof data.exp === "number" && Date.now() > data.exp) {
    throw new Error("Sign-in expired. Tap Sign in again.");
  }
  if (!data.idToken && !data.accessToken) {
    throw new Error("Sign-in handoff incomplete. Try again.");
  }
  return data;
}

export async function completeAuthBridgeWithTokens(
  idToken?: string | null,
  accessToken?: string | null,
): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase not configured.");
  const credential = GoogleAuthProvider.credential(idToken ?? null, accessToken ?? null);
  await signInWithCredential(auth, credential);
}

export async function completeAuthBridgeWithIdToken(idToken: string): Promise<void> {
  await completeAuthBridgeWithTokens(idToken, null);
}

export async function completeAuthBridgeFromDeepLink(url: string): Promise<boolean> {
  const parsed = parseAuthBridgeDeepLink(url);
  if (!parsed) return false;
  if (parsed.code) {
    saveSession(parsed.code);
    const data = await consumeHandoff(parsed.code);
    await completeAuthBridgeWithTokens(data.idToken, data.accessToken);
    clearSession();
    return true;
  }
  if (parsed.idToken) {
    await completeAuthBridgeWithIdToken(parsed.idToken);
    clearSession();
    return true;
  }
  return false;
}

export async function closeAndroidAuthBrowser(): Promise<void> {
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch {
    /* already closed */
  }
}

async function claimSession(session: string): Promise<boolean> {
  const data = await readHandoff(session);
  if (!data?.idToken && !data?.accessToken) return false;
  const consumed = await consumeHandoff(session);
  await completeAuthBridgeWithTokens(consumed.idToken, consumed.accessToken);
  clearSession();
  await closeAndroidAuthBrowser();
  return true;
}

/**
 * If a previous Sign-in left a session (WebView reload / process pause),
 * finish it when the app becomes active again.
 */
export async function resumePendingAndroidAuthHandoff(): Promise<boolean> {
  if (!isCapacitorAndroid()) return false;
  const auth = getFirebaseAuth();
  if (auth?.currentUser && !auth.currentUser.isAnonymous) {
    clearSession();
    return false;
  }
  const session = loadSession();
  if (!session) return false;
  try {
    return await claimSession(session);
  } catch {
    return false;
  }
}

/**
 * Opens the live auth bridge and waits until Firestore handoff completes.
 */
export async function startAndroidGoogleAuthBridge(): Promise<void> {
  const session = newSessionId();
  saveSession(session);

  const { Browser } = await import("@capacitor/browser");
  const bridgeUrl = `${AUTH_BRIDGE_URL}?session=${encodeURIComponent(session)}`;

  let settled = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const unsubscribers: Array<() => void> = [];

  const cleanup = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    for (const u of unsubscribers) {
      try {
        u();
      } catch {
        /* */
      }
    }
    unsubscribers.length = 0;
  };

  await new Promise<void>((resolve, reject) => {
    const succeed = async () => {
      if (settled) return;
      settled = true;
      cleanup();
      try {
        const ok = await claimSession(session);
        if (!ok) throw new Error("Sign-in handoff incomplete. Try again.");
        resolve();
      } catch (e) {
        clearSession();
        reject(e instanceof Error ? e : new Error("Sign-in failed."));
      }
    };

    const fail = (err: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      clearSession();
      void closeAndroidAuthBrowser();
      reject(err instanceof Error ? err : new Error("Sign-in failed."));
    };

    const tick = () => {
      void (async () => {
        if (settled) return;
        try {
          const data = await readHandoff(session);
          if (data?.idToken || data?.accessToken) await succeed();
        } catch {
          /* keep polling */
        }
      })();
    };

    timer = setInterval(tick, HANDOFF_POLL_MS);
    timeoutId = setTimeout(() => {
      fail(new Error("Sign-in timed out. Tap Sign in and finish Google in the browser."));
    }, HANDOFF_TIMEOUT_MS);

    void import("@capacitor/app")
      .then(({ App }) => {
        const sub = App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) tick();
        });
        void sub.then((h) => unsubscribers.push(() => void h.remove()));
        const urlSub = App.addListener("appUrlOpen", ({ url }) => {
          const parsed = parseAuthBridgeDeepLink(url);
          if (parsed?.code) {
            saveSession(parsed.code);
            tick();
          }
        });
        void urlSub.then((h) => unsubscribers.push(() => void h.remove()));
      })
      .catch(() => {
        /* */
      });

    void Browser.addListener("browserFinished", () => {
      tick();
    }).then((h) => unsubscribers.push(() => void h.remove()));

    void Browser.open({ url: bridgeUrl, presentationStyle: "popover" })
      .then(() => {
        setTimeout(tick, 1200);
      })
      .catch((e) => fail(e));
  });
}
